import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useThemeMode } from "../../ThemeContext";
import { APP_URLS } from "../../helpers/url_helper";
import { dataAPI } from "../../helpers/api_service";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const FIELD_TYPES = [
  { value: "text", label: "Short Answer" },
  { value: "textarea", label: "Paragraph" },
  { value: "radio", label: "Option Button (Single Choice)" },
  { value: "checkbox", label: "Checkbox (Multi Choice)" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multi_select", label: "Multiple select" },
  { value: "attachment", label: "Attachment" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
];

const DATA_SOURCE_CHOICES = [
  { value: "employees", label: "Employee list" },
  { value: "projects", label: "Project" },
  { value: "assets", label: "Assets" },
  { value: "tasks", label: "Task" },
  { value: "site", label: "Site" },
];

const FORM_DESTINATION_CHOICES = [
  { value: "user_profile", label: "User profile" },
  { value: "selected_asset", label: "Select Assets" },
  { value: "none", label: "None" },
];

const isOptionType = (type) =>
  ["dropdown", "multi_select", "checkbox", "radio"].includes(type);
const supportsDataSourceType = (type) =>
  ["dropdown", "multi_select"].includes(type);
const getNormalizedDataSource = (field) => {
  if (!supportsDataSourceType(field?.type)) return "";
  const rawSource = String(field?.dataSource || "").trim().toLowerCase();
  if (!rawSource || rawSource === "custom") return "";
  if (rawSource === "sites") return "site";
  return DATA_SOURCE_CHOICES.some((choice) => choice.value === rawSource)
    ? rawSource
    : "";
};

const buildDefaultField = (index = 0) => ({
  id: `field-${Date.now()}-${index}`,
  label: "",
  type: "text",
  required: false,
  placeholder: "",
  optionMode: "manual",
  dataSource: "",
  options: ["Option 1", "Option 2"],
});

const buildDefaultSection = (index = 0) => ({
  id: `section-${Date.now()}-${index}`,
  title: `Section ${index + 1}`,
  description: "",
  fields: [buildDefaultField(1)],
});

const normalizeField = (field, index = 0) => {
  const normalizedDataSource = getNormalizedDataSource(field);
  return {
    id: field?.id || `field-${Date.now()}-${index}`,
    label: field?.label || "",
    type: field?.type || "text",
    required: Boolean(field?.required),
    placeholder: field?.placeholder || "",
    optionMode: normalizedDataSource ? "dataSource" : "manual",
    dataSource: normalizedDataSource,
    options:
      Array.isArray(field?.options) && field.options.length
        ? field.options
        : isOptionType(field?.type)
          ? ["Option 1", "Option 2"]
          : [],
  };
};

function MultiSelectFormPreview({ field, mode }) {
  const edge = mode === "dark" ? "#3a3a3a" : "#d0d5dd";
  const rowBorder = mode === "dark" ? "#2a2a2a" : "#e5e7eb";
  const manualOpts = (field.options || []).map(String).filter(Boolean);
  const sourceLabel =
    DATA_SOURCE_CHOICES.find((c) => c.value === (field.dataSource || ""))?.label ||
    "data source";

  const rows =
    field.optionMode === "dataSource"
      ? [
          { text: `Choices loaded from: ${sourceLabel}`, muted: true },
          { text: "Example option A", muted: false },
          { text: "Example option B", muted: false },
        ]
      : manualOpts.length > 0
        ? manualOpts.map((t) => ({ text: t, muted: false }))
        : [{ text: "Add options in Manual options above", muted: true }];

  const surface = mode === "dark" ? "#191919" : "#ffffff";

  return (
    <details
      style={{
        border: `1px solid ${edge}`,
        borderRadius: 8,
        overflow: "hidden",
        maxWidth: 440,
        background: surface,
      }}
    >
      <summary
        style={{
          padding: "10px",
          fontSize: 13,
          fontWeight: 600,
          color: mode === "dark" ? "#e4e7ec" : "#334155",
          cursor: "pointer",
          listStyle: "none",
        }}
      >
        Select one or more options
      </summary>
      <div
        style={{
          borderTop: `1px solid ${rowBorder}`,
          background: surface,
          maxHeight: 220,
          overflowY: "auto",
        }}
      >
        {rows.map((row, index) => (
          <div
            key={`${field.id}-preview-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 10px",
              borderBottom:
                index < rows.length - 1 ? `1px solid ${rowBorder}` : "none",
              opacity: row.muted ? 0.85 : 1,
            }}
          >
            <input
              type="checkbox"
              disabled
              tabIndex={-1}
              aria-hidden
              style={{ outline: "none", margin: 0, accentColor: "#009391" }}
            />
            <span
              style={{
                fontSize: 13,
                color: mode === "dark" ? "#e4e7ec" : "#334155",
              }}
            >
              {row.text}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}

function SortableSection({ sectionId, mode, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sectionId,
  });

  return (
    <section
      ref={setNodeRef}
      style={{
        ...sectionCardStyle(mode),
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 3 : 1,
      }}
    >
      {children({ attributes, listeners })}
    </section>
  );
}

function SortableQuestion({ questionId, mode, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: questionId,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        ...questionRowCardStyle(mode),
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 2 : 1,
      }}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function UseTemplateBuilder() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const template = location.state?.template || null;
  const isEditMode = Boolean(location.state?.isEdit && template?.client_form_id);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const [formTitle, setFormTitle] = useState(
    template?.title || "Untitled form",
  );
  const [formDescription, setFormDescription] = useState(
    template?.description || "",
  );
  const [formDestination, setFormDestination] = useState(
    template?.form_destination || "none",
  );
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessageType, setSaveMessageType] = useState("success");
  const [isSaving, setIsSaving] = useState(false);
  const [sections, setSections] = useState(() => {
    const source = Array.isArray(template?.schema_json) ? template.schema_json : [];

    if (!source.length) {
      return [buildDefaultSection(0)];
    }

    const isSectionSchema = Array.isArray(source[0]?.fields);
    if (!isSectionSchema) {
      return [
        {
          id: `section-${Date.now()}-0`,
          title: "Section 1",
          description: "",
          fields: source.map((field, idx) => normalizeField(field, idx)),
        },
      ];
    }

    return source.map((section, sectionIdx) => ({
      id: section?.id || `section-${Date.now()}-${sectionIdx}`,
      title: section?.title || `Section ${sectionIdx + 1}`,
      description: section?.description || "",
      fields:
        Array.isArray(section?.fields) && section.fields.length
          ? section.fields.map((field, idx) => normalizeField(field, idx))
          : [buildDefaultField(1)],
    }));
  });
  const [editingQuestionIds, setEditingQuestionIds] = useState(() => {
    const initial = {};
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        initial[field.id] = false;
      });
    });
    return initial;
  });

  const canRenderBuilder = useMemo(
    () => template && Array.isArray(template.schema_json),
    [template],
  );

  const getTypeLabel = (typeValue) =>
    FIELD_TYPES.find((type) => type.value === typeValue)?.label || "Short Answer";

  const updateSection = (sectionId, updates) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section,
      ),
    );
  };

  const updateField = (sectionId, fieldId, updates) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) =>
            field.id === fieldId ? { ...field, ...updates } : field,
          ),
        };
      }),
    );
  };

  const handleTypeChange = (sectionId, field, nextType) => {
    if (nextType === "attachment") {
      updateField(sectionId, field.id, {
        type: "attachment",
        optionMode: "manual",
        dataSource: "",
        options: [],
        placeholder:
          field.placeholder?.trim() ||
          "Upload a PDF, Word document (.doc, .docx), or an image (PNG, JPG, GIF, WebP).",
      });
      return;
    }

    if (!isOptionType(nextType)) {
      updateField(sectionId, field.id, {
        type: nextType,
        optionMode: "manual",
        dataSource: "",
        options: [],
      });
      return;
    }

    updateField(sectionId, field.id, {
      type: nextType,
      optionMode:
        supportsDataSourceType(nextType) && field.optionMode === "dataSource"
          ? "dataSource"
          : "manual",
      dataSource:
        supportsDataSourceType(nextType) && field.optionMode === "dataSource"
          ? field.dataSource || "employees"
          : "",
      options: field.options?.length ? field.options : ["Option 1", "Option 2"],
    });
  };

  const addSection = () => {
    setSections((prev) => [...prev, buildDefaultSection(prev.length)]);
  };

  const removeSection = (sectionId) => {
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const addQuestion = (sectionId) => {
    const newField = buildDefaultField(Math.floor(Math.random() * 1000));
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return { ...section, fields: [...section.fields, newField] };
      }),
    );
    setEditingQuestionIds((prev) => ({ ...prev, [newField.id]: true }));
  };

  const removeQuestion = (sectionId, fieldId) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.filter((field) => field.id !== fieldId),
        };
      }),
    );
    setEditingQuestionIds((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const duplicateQuestion = (sectionId, field) => {
    const duplicatedField = {
      ...field,
      id: `field-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      label: field.label ? `${field.label} (copy)` : "Untitled question",
    };
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return { ...section, fields: [...section.fields, duplicatedField] };
      }),
    );
    setEditingQuestionIds((prev) => ({ ...prev, [duplicatedField.id]: false }));
  };

  const toggleQuestionEdit = (fieldId) => {
    setEditingQuestionIds((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  const handleSaveTemplateForm = async () => {
    const trimmedTitle = formTitle.trim();
    if (!trimmedTitle) {
      setSaveMessageType("error");
      setSaveMessage("Form title is required.");
      return;
    }
    if (!sections.length) {
      setSaveMessageType("error");
      setSaveMessage("Please add at least one section.");
      return;
    }
    const totalQuestions = sections.reduce(
      (acc, section) => acc + (section.fields?.length || 0),
      0,
    );
    if (!totalQuestions) {
      setSaveMessageType("error");
      setSaveMessage("Please add at least one question.");
      return;
    }

    const templateId = template?.id;
    if (!templateId) {
      setSaveMessageType("error");
      setSaveMessage("Template id is missing. Please reopen this form from template library.");
      return;
    }

    const schemaJson = sections.map((section, sectionIndex) => ({
      id: section.id,
      title: (section.title || "").trim() || `Section ${sectionIndex + 1}`,
      description: (section.description || "").trim(),
      section_order: sectionIndex + 1,
      fields: (section.fields || []).map((field, questionIndex) => {
        const baseField = {
          id: field.id,
          label: (field.label || "").trim(),
          type: field.type,
          required: field.required,
          question_order: questionIndex + 1,
        };

        if (!isOptionType(field.type)) {
          return { ...baseField, placeholder: field.placeholder || "" };
        }

        if (
          supportsDataSourceType(field.type) &&
          field.optionMode === "dataSource"
        ) {
          return {
            ...baseField,
            dataSource: field.dataSource || "employees",
            options: [],
          };
        }

        let manualOptions = (field.options || [])
          .map((option) => option.trim())
          .filter(Boolean);

        if (
          field.type === "dropdown" &&
          manualOptions.length > 0 &&
          !manualOptions.some((opt) => opt.toLowerCase() === "other")
        ) {
          manualOptions = [...manualOptions, "Other"];
        }

        return { ...baseField, options: manualOptions };
      }),
    }));

    const payload = {
      template_id: templateId,
      title: trimmedTitle,
      description: formDescription.trim(),
      form_destination: formDestination || "none",
      schema_json: schemaJson,
      status: "publish",
      is_active: true,
    };

    try {
      setIsSaving(true);
      setSaveMessage("");
      if (isEditMode) {
        await dataAPI.updateDigitalFormClientForm(template.client_form_id, payload);
      } else {
        await dataAPI.createDigitalFormClientForm(payload);
      }
      setSaveMessageType("success");
      setSaveMessage(
        isEditMode
          ? "Client form updated successfully."
          : "Client form created successfully.",
      );
    } catch (error) {
      setSaveMessageType("error");
      setSaveMessage(
        error?.message ||
          (isEditMode ? "Failed to update form." : "Failed to create form."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const addOption = (sectionId, fieldId) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) => {
            if (field.id !== fieldId) return field;
            const nextIndex = (field.options?.length || 0) + 1;
            return {
              ...field,
              options: [...(field.options || []), `Option ${nextIndex}`],
            };
          }),
        };
      }),
    );
  };

  const updateOption = (sectionId, fieldId, optionIndex, value) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) => {
            if (field.id !== fieldId) return field;
            const nextOptions = [...(field.options || [])];
            nextOptions[optionIndex] = value;
            return { ...field, options: nextOptions };
          }),
        };
      }),
    );
  };

  const removeOption = (sectionId, fieldId, optionIndex) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          fields: section.fields.map((field) => {
            if (field.id !== fieldId) return field;
            const nextOptions = (field.options || []).filter(
              (_, idx) => idx !== optionIndex,
            );
            return {
              ...field,
              options: nextOptions.length ? nextOptions : ["Option 1"],
            };
          }),
        };
      }),
    );
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSections((prev) => {
      const oldIndex = prev.findIndex((section) => section.id === active.id);
      const newIndex = prev.findIndex((section) => section.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleQuestionDragEnd = (sectionId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        const oldIndex = section.fields.findIndex((field) => field.id === active.id);
        const newIndex = section.fields.findIndex((field) => field.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return section;
        return {
          ...section,
          fields: arrayMove(section.fields, oldIndex, newIndex),
        };
      }),
    );
  };

  if (!canRenderBuilder) {
    return (
      <div style={{ padding: "24px 28px" }}>
        <div style={emptyStateStyle(mode)}>
          No template selected. Please go back and choose a template first.
        </div>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.DIGITAL_FORM_MANAGEMENT)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to library
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div style={layoutStyle()}>
        <aside style={instructionPanelStyle(mode)}>
          <h3 style={instructionTitleStyle(mode)}>Form Builder Help</h3>
          <p style={instructionTextStyle(mode)}>
            Quick guide to build clear, accurate forms: create sections first, then add focused questions inside each section. Use the right response type for expected input, configure options for choice fields, and mark required only when truly needed.
          </p>

          <div style={instructionSectionTitleStyle(mode)}>Response Type Use Cases</div>
          <ul style={instructionListStyle(mode)}>
            <li><strong>Short Answer</strong>: Names, IDs, short comments, serial numbers.</li>
            <li><strong>Paragraph</strong>: Detailed notes, incident description, handover remarks.</li>
            <li><strong>Option Button (Single Choice)</strong>: One valid choice only (Pass/Fail, Yes/No).</li>
            <li><strong>Checkbox (Multi Choice)</strong>: Multiple selections allowed (PPE items, completed checks).</li>
            <li><strong>Dropdown</strong>: Long option list where one value is selected (site, category, department).</li>
            <li><strong>Multiple select</strong>: Same list as a dropdown-style panel; respondents tick several options (checkboxes).</li>
            <li><strong>Date</strong>: Inspection date, due date, service date, expiry date.</li>
            <li><strong>Number</strong>: Meter reading, quantity, temperature, score, runtime hours.</li>
            <li><strong>Email</strong>: Contact email for notifier, approver, or assignee.</li>
          </ul>

          <div style={instructionSectionTitleStyle(mode)}>Choice Field Setup</div>
          <ul style={instructionListStyle(mode)}>
            <li><strong>Manual options</strong>: Use for fixed values managed by form owner.</li>
            <li><strong>Data source</strong>: Use when options should auto-load from live records.</li>
          </ul>
          <div style={instructionNoteStyle(mode)}>
            Data source options available: Employee list, Project, Assets, Task, Site. When Data source is selected, static manual options are not required.
          </div>
        </aside>

        <main style={builderColumnStyle()}>
          <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate(APP_URLS.DIGITAL_FORM_MANAGEMENT)}
              style={backButtonStyle(mode)}
            >
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back to library
            </button>
            <button
              type="button"
              onClick={handleSaveTemplateForm}
              style={saveButtonStyle(isSaving)}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : isEditMode ? "Update Form" : "Save Form"}
            </button>
          </div>

          {saveMessage && (
            <div style={saveMessageStyle(mode, saveMessageType)}>{saveMessage}</div>
          )}

          <section style={formHeaderCardStyle(mode)}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 12 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <label style={headerLabelStyle(mode)}>Form title</label>
            <input
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              style={titleInputStyle(mode)}
              placeholder="Form title"
            />
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            <label style={headerLabelStyle(mode)}>Form destination</label>
            <select
              value={formDestination}
              onChange={(e) => setFormDestination(e.target.value)}
              style={selectStyle(mode)}
            >
              {FORM_DESTINATION_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <label style={headerLabelStyle(mode)}>Description</label>
          <textarea
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            style={descInputStyle(mode)}
            placeholder="Form description"
            rows={3}
          />
        </div>
          </section>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext
              items={sections.map((section) => section.id)}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ display: "grid", gap: 14 }}>
                {sections.map((section, sectionIndex) => (
                  <SortableSection key={section.id} sectionId={section.id} mode={mode}>
                    {({ attributes: sectionAttributes, listeners: sectionListeners }) => (
                      <>
                        <div style={sectionHeaderTopStyle()}>
                          <div style={sectionHeadingLeftStyle()}>
                            <span
                              {...sectionAttributes}
                              {...sectionListeners}
                              style={dragHandleStyle(mode)}
                              title="Drag to reorder section"
                            >
                              <DragIndicatorIcon style={{ fontSize: 15 }} />
                            </span>
                            <div>
                              <div style={sectionTitleStyle(mode)}>
                                Section {sectionIndex + 1}
                              </div>
                              <div style={sectionMetaStyle(mode)}>
                                {section.fields.length} questions
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "inline-flex", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() => removeSection(section.id)}
                              style={sectionDeleteBtnStyle()}
                              title="Remove section"
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </button>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 16,
                          }}
                        >
                          <div style={{ display: "grid", gap: 6 }}>
                            <label style={headerLabelStyle(mode)}>Section title</label>
                            <input
                              value={section.title}
                              onChange={(e) =>
                                updateSection(section.id, { title: e.target.value })
                              }
                              style={inputStyle(mode)}
                              placeholder={`Section ${sectionIndex + 1}`}
                            />
                          </div>
                          <div style={{ display: "grid", gap: 6 }}>
                            <label style={headerLabelStyle(mode)}>
                              Section description (optional)
                            </label>
                            <input
                              value={section.description}
                              onChange={(e) =>
                                updateSection(section.id, { description: e.target.value })
                              }
                              style={inputStyle(mode)}
                              placeholder="Short section description"
                            />
                          </div>
                        </div>

                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleQuestionDragEnd(section.id, event)}
                        >
                          <SortableContext
                            items={section.fields.map((field) => field.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div style={{ display: "grid", gap: 10 }}>
                              {section.fields.map((field, fieldIndex) => {
                                const isEditing = Boolean(editingQuestionIds[field.id]);
                                return (
                                  <SortableQuestion key={field.id} questionId={field.id} mode={mode}>
                                    {({ attributes, listeners }) => (
                                      <>
                                        <div style={questionRowHeaderStyle()}>
                                          <div style={questionRowLeftStyle()}>
                                            <span
                                              {...attributes}
                                              {...listeners}
                                              style={dragHandleStyle(mode)}
                                              title="Drag to reorder question"
                                            >
                                              <DragIndicatorIcon style={{ fontSize: 15 }} />
                                            </span>
                                            <span style={questionOrderStyle(mode)}>{fieldIndex + 1}</span>
                                            <div>
                                              <div style={questionTitleTextStyle(mode)}>
                                                {field.label || "Untitled question"}
                                              </div>
                                              <div style={questionTypeTextStyle(mode)}>
                                                {getTypeLabel(field.type)}
                                              </div>
                                            </div>
                                          </div>
                                          <div style={questionActionRowStyle()}>
                                            <button
                                              type="button"
                                              onClick={() => toggleQuestionEdit(field.id)}
                                              style={inlineActionBtnStyle(mode)}
                                            >
                                              {isEditing ? (
                                                <>
                                                  <CloseIcon style={{ fontSize: 14 }} />
                                                  Close
                                                </>
                                              ) : (
                                                <>
                                                  <EditOutlinedIcon style={{ fontSize: 14 }} />
                                                  Edit
                                                </>
                                              )}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => duplicateQuestion(section.id, field)}
                                              style={inlineActionBtnStyle(mode)}
                                            >
                                              <ContentCopyOutlinedIcon style={{ fontSize: 14 }} />
                                              Duplicate
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => removeQuestion(section.id, field.id)}
                                              style={deleteSquareBtnStyle()}
                                              title="Delete question"
                                            >
                                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                            </button>
                                          </div>
                                        </div>

                                        {isEditing && (
                                          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
                                            <div
                                              style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 220px 180px",
                                                gap: 12,
                                              }}
                                            >
                                              <div style={{ display: "grid", gap: 6 }}>
                                                <label style={headerLabelStyle(mode)}>Question</label>
                                                <input
                                                  value={field.label}
                                                  onChange={(e) =>
                                                    updateField(section.id, field.id, {
                                                      label: e.target.value,
                                                    })
                                                  }
                                                  style={questionInputStyle(mode)}
                                                  placeholder={`Question ${fieldIndex + 1}`}
                                                />
                                              </div>
                                              <div style={{ display: "grid", gap: 6 }}>
                                                <label style={headerLabelStyle(mode)}>Response type</label>
                                                <select
                                                  value={field.type}
                                                  onChange={(e) =>
                                                    handleTypeChange(section.id, field, e.target.value)
                                                  }
                                                  style={selectStyle(mode)}
                                                >
                                                  {FIELD_TYPES.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                      {type.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div style={{ display: "flex", alignItems: "flex-end" }}>
                                                <label style={requiredLabelStyle(mode)}>
                                                  <input
                                                    type="checkbox"
                                                    checked={field.required}
                                                    onChange={(e) =>
                                                      updateField(section.id, field.id, {
                                                        required: e.target.checked,
                                                      })
                                                    }
                                                    style={{ accentColor: "#009391" }}
                                                  />
                                                  Mark as required
                                                </label>
                                              </div>
                                            </div>

                                            {!isOptionType(field.type) && (
                                              <div style={{ display: "grid", gap: 6 }}>
                                                <label style={headerLabelStyle(mode)}>
                                                  {field.type === "attachment"
                                                    ? "Helper text (optional)"
                                                    : "Placeholder"}
                                                </label>
                                                <input
                                                  value={field.placeholder}
                                                  onChange={(e) =>
                                                    updateField(section.id, field.id, {
                                                      placeholder: e.target.value,
                                                    })
                                                  }
                                                  style={inputStyle(mode)}
                                                  placeholder={
                                                    field.type === "attachment"
                                                      ? "Shown above the file upload on the live form"
                                                      : "Optional placeholder text"
                                                  }
                                                />
                                                {field.type === "attachment" ? (
                                                  <p
                                                    style={{
                                                      margin: 0,
                                                      fontSize: 12,
                                                      lineHeight: 1.45,
                                                      color: mode === "dark" ? "#98a2b3" : "#667085",
                                                    }}
                                                  >
                                                    Respondents can upload{" "}
                                                    <strong>PDF</strong>, <strong>Word</strong> (.doc,
                                                    .docx), or <strong>images</strong> (PNG, JPG, GIF,
                                                    WebP). Maximum size on submit: 8 MB.
                                                  </p>
                                                ) : null}
                                              </div>
                                            )}

                                            {isOptionType(field.type) && (
                                              <div style={{ display: "grid", gap: 10 }}>
                                                {supportsDataSourceType(field.type) ? (
                                                  <div
                                                    style={{
                                                      display: "inline-flex",
                                                      border: `1px solid ${mode === "dark" ? "#3a3a3a" : "#d0d5dd"}`,
                                                      borderRadius: 10,
                                                      overflow: "hidden",
                                                      width: "fit-content",
                                                    }}
                                                  >
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        updateField(section.id, field.id, {
                                                          optionMode: "manual",
                                                          dataSource: "",
                                                        })
                                                      }
                                                      style={modeToggleButtonStyle(
                                                        mode,
                                                        field.optionMode === "manual",
                                                      )}
                                                    >
                                                      {field.type === "dropdown"
                                                        ? "Custome Options"
                                                        : "Manual options"}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        updateField(section.id, field.id, {
                                                          optionMode: "dataSource",
                                                          dataSource: field.dataSource || "employees",
                                                        })
                                                      }
                                                      style={modeToggleButtonStyle(
                                                        mode,
                                                        field.optionMode === "dataSource",
                                                      )}
                                                    >
                                                      Data source
                                                    </button>
                                                  </div>
                                                ) : null}

                                                {!supportsDataSourceType(field.type) ||
                                                field.optionMode === "manual" ? (
                                                  <div style={{ display: "grid", gap: 8 }}>
                                                    {(field.options || []).map((option, optionIndex) => (
                                                      <div
                                                        key={`${field.id}-option-${optionIndex}`}
                                                        style={{
                                                          display: "grid",
                                                          gridTemplateColumns: "1fr 38px",
                                                          gap: 8,
                                                          alignItems: "center",
                                                        }}
                                                      >
                                                        <input
                                                          value={option}
                                                          onChange={(e) =>
                                                            updateOption(
                                                              section.id,
                                                              field.id,
                                                              optionIndex,
                                                              e.target.value,
                                                            )
                                                          }
                                                          style={inputStyle(mode)}
                                                          placeholder={`Option ${optionIndex + 1}`}
                                                        />
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            removeOption(section.id, field.id, optionIndex)
                                                          }
                                                          style={smallIconBtnStyle(mode)}
                                                          title="Remove option"
                                                        >
                                                          <RemoveCircleOutlineIcon style={{ fontSize: 18 }} />
                                                        </button>
                                                      </div>
                                                    ))}
                                                    <button
                                                      type="button"
                                                      onClick={() => addOption(section.id, field.id)}
                                                      style={addOptionBtnStyle(mode)}
                                                    >
                                                      <AddIcon style={{ fontSize: 16 }} />
                                                      Add option
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <select
                                                    value={field.dataSource || "employees"}
                                                    onChange={(e) =>
                                                      updateField(section.id, field.id, {
                                                        dataSource: e.target.value,
                                                        options: [],
                                                      })
                                                    }
                                                    style={selectStyle(mode)}
                                                  >
                                                    {DATA_SOURCE_CHOICES.map((source) => (
                                                      <option key={source.value} value={source.value}>
                                                        {source.label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                )}
                                              </div>
                                            )}

                                            {field.type === "multi_select" ? (
                                              <>
                                                <div
                                                  style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    letterSpacing: "0.03em",
                                                    marginBottom: 6,
                                                    color: mode === "dark" ? "#94a3b8" : "#64748b",
                                                  }}
                                                >
                                                  Form preview (multiple select)
                                                </div>
                                                <MultiSelectFormPreview field={field} mode={mode} />
                                              </>
                                            ) : null}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </SortableQuestion>
                                );
                              })}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                onClick={() => addQuestion(section.id)}
                                style={addQuestionBtnStyle()}
                              >
                                <AddIcon style={{ fontSize: 16 }} />
                                Add question
                              </button>
                            </div>
                          </SortableContext>
                        </DndContext>
                      </>
                    )}
                  </SortableSection>
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={addSection} style={addFieldButtonStyle()}>
              <AddIcon style={{ fontSize: 18 }} />
              Add section
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

const emptyStateStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#3e3e3e" : "#e3e6e9"}`,
  borderRadius: 10,
  padding: 16,
  color: mode === "dark" ? "#d5d5d5" : "#555",
  background: mode === "dark" ? "#1a1a1a" : "#fff",
  marginBottom: 12,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const formHeaderCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderTop: "8px solid #009391",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  marginBottom: 14,
  display: "grid",
  gap: 12,
});

const sectionCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 14,
  display: "grid",
  gap: 12,
});

const sectionHeaderTopStyle = () => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
});

const sectionHeadingLeftStyle = () => ({
  display: "inline-flex",
  alignItems: "flex-start",
  gap: 10,
});

const sectionTitleStyle = (mode) => ({
  fontSize: 22,
  fontWeight: 700,
  color: mode === "dark" ? "#f2f4f7" : "#1d2939",
  lineHeight: 1.1,
});

const sectionMetaStyle = (mode) => ({
  fontSize: 14,
  marginTop: 4,
  color: mode === "dark" ? "#98a2b3" : "#667085",
});

const addQuestionBtnStyle = () => ({
  border: "none",
  borderRadius: 6,
  background: "#009391",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 11px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
});

const sectionDeleteBtnStyle = () => ({
  border: "1px solid #f5c2c7",
  borderRadius: 6,
  background: "#fce8ea",
  color: "#c92a2a",
  width: 32,
  height: 32,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const questionRowCardStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#eceef1"}`,
  borderRadius: 10,
  background: mode === "dark" ? "#161616" : "#fafafa",
  padding: 12,
});

const questionRowHeaderStyle = () => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
});

const questionRowLeftStyle = () => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
});

const questionOrderStyle = (mode) => ({
  fontSize: 12,
  fontWeight: 600,
  color: mode === "dark" ? "#b0b6c3" : "#667085",
  minWidth: 16,
});

const questionTitleTextStyle = (mode) => ({
  fontSize: 15,
  fontWeight: 600,
  color: mode === "dark" ? "#f2f4f7" : "#1d2939",
});

const questionTypeTextStyle = (mode) => ({
  fontSize: 13,
  color: mode === "dark" ? "#98a2b3" : "#667085",
  marginTop: 2,
});

const questionActionRowStyle = () => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
});

const inlineActionBtnStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#3a3a3a" : "#e4e7ec"}`,
  borderRadius: 6,
  background: mode === "dark" ? "#1b1b1b" : "#fff",
  color: mode === "dark" ? "#e4e7ec" : "#1f2937",
  fontSize: 13,
  padding: "6px 10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const deleteSquareBtnStyle = () => ({
  border: "1px solid #f5c2c7",
  borderRadius: 6,
  background: "#fce8ea",
  color: "#c92a2a",
  width: 32,
  height: 32,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const dragHandleStyle = (mode) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  borderRadius: 6,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  cursor: "grab",
  userSelect: "none",
});

const requiredLabelStyle = (mode) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  fontSize: 12,
});

const titleInputStyle = (mode) => ({
  width: "100%",
  fontSize: 18,
  fontWeight: 600,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  outline: "none",
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "10px 12px",
});

const descInputStyle = (mode) => ({
  width: "100%",
  fontSize: 14,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  outline: "none",
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#bdbdbd" : "#667085",
  padding: "10px 12px",
  resize: "vertical",
});

const headerLabelStyle = (mode) => ({
  fontSize: 13,
  fontWeight: 600,
  color: mode === "dark" ? "#e4e7ec" : "#344054",
});

const questionInputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "10px 12px",
  fontSize: 14,
  fontWeight: 500,
});

const inputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const selectStyle = (mode) => ({
  ...inputStyle(mode),
  padding: "9px 10px",
});

const modeToggleButtonStyle = (mode, active) => ({
  border: "none",
  background: active ? "#009391" : mode === "dark" ? "#191919" : "#fff",
  color: active ? "#fff" : mode === "dark" ? "#d0d5dd" : "#344054",
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
  fontWeight: 500,
});

const smallIconBtnStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#1d1d1d" : "#fff",
  color: mode === "dark" ? "#fda29b" : "#b42318",
  width: 36,
  height: 36,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const addOptionBtnStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#3f3f3f" : "#e4e7ec"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#1a1a1a" : "#fafafa",
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  fontWeight: 500,
  fontSize: 13,
  padding: "8px 10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  width: "fit-content",
});

const addFieldButtonStyle = () => ({
  border: "none",
  borderRadius: 8,
  background: "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const layoutStyle = () => ({
  display: "grid",
  gridTemplateColumns: "65% 35%",
  gap: 18,
  alignItems: "start",
});

const builderColumnStyle = () => ({
  maxWidth: 920,
  order: 1,
});

const instructionPanelStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#d7dbe0"}`,
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  position: "sticky",
  top: 76,
  order: 2,
});

const instructionTitleStyle = (mode) => ({
  margin: 0,
  marginBottom: 10,
  fontSize: 16,
  fontWeight: 700,
  color: mode === "dark" ? "#fff" : "#1d2939",
});

const instructionTextStyle = (mode) => ({
  margin: 0,
  marginBottom: 10,
  fontSize: 13,
  color: mode === "dark" ? "#bdbdbd" : "#667085",
  lineHeight: 1.5,
});

const instructionListStyle = (mode) => ({
  margin: 0,
  paddingLeft: 18,
  display: "grid",
  gap: 6,
  color: mode === "dark" ? "#d0d5dd" : "#344054",
  fontSize: 13,
  lineHeight: 1.5,
});

const instructionSectionTitleStyle = (mode) => ({
  marginTop: 12,
  marginBottom: 8,
  fontSize: 13,
  fontWeight: 700,
  color: mode === "dark" ? "#e4e7ec" : "#1d2939",
});

const instructionNoteStyle = (mode) => ({
  marginTop: 12,
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#e5e7eb"}`,
  background: mode === "dark" ? "#161616" : "#fafafa",
  color: mode === "dark" ? "#98a3b8" : "#667085",
  fontSize: 12,
});

const saveButtonStyle = (disabled = false) => ({
  border: "none",
  borderRadius: 8,
  background: disabled ? "#73b9b8" : "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "9px 14px",
  cursor: disabled ? "not-allowed" : "pointer",
});

const saveMessageStyle = (mode, type) => ({
  marginBottom: 12,
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
  border:
    type === "error"
      ? `1px solid ${mode === "dark" ? "#7a3d3d" : "#f1b2b2"}`
      : `1px solid ${mode === "dark" ? "#2e6b69" : "#9be3df"}`,
  background:
    type === "error"
      ? mode === "dark"
        ? "#2b1717"
        : "#fff3f3"
      : mode === "dark"
        ? "#122c2b"
        : "#ecfbfa",
  color:
    type === "error"
      ? mode === "dark"
        ? "#ffb0b0"
        : "#a53030"
      : mode === "dark"
        ? "#8de0db"
        : "#0c6a65",
});

export default UseTemplateBuilder;
